import type { RouteRecordRaw } from 'vue-router';
import { isNavigationFailure, Router } from 'vue-router';
import { useUserStoreWidthOut } from '@/store/modules/user';
import { useAsyncRouteStoreWidthOut } from '@/store/modules/asyncRoute';
import { ACCESS_TOKEN } from '@/store/mutation-types';
import { storage } from '@/utils/Storage';
import { PageEnum } from '@/enums/pageEnum';
import { ErrorPageRoute } from '@/router/base';
import { useLoadingBar } from '@/hooks/web/useLoadingBar';

const LOGIN_PATH = PageEnum.BASE_LOGIN;
const DEFAULT_HOME = PageEnum.BASE_HOME_REDIRECT; // /dashboard/console
const whitePathList = [LOGIN_PATH];
const loadingBar = useLoadingBar();

/**
 * Handle mobile deployment mode redirects for ongoing navigation.
 * In mobile mode, redirects non-deployment pages to the aggregated view.
 * Returns true if a redirect was triggered (caller should return early).
 */
function handleMobileDeploymentRedirect(targetPath: string): boolean {
  const isMobile = sessionStorage.getItem('IS_MOBILE');

  if (isMobile === 'true' && !targetPath.startsWith('/deployment/') && !targetPath.startsWith('/setting/')) {
    window.location.href = '/#/deployment/equipment-aggregated';
    window.location.reload();
    return true;
  }

  return false;
}

export function createRouterGuards(router: Router) {
  const userStore = useUserStoreWidthOut();
  const asyncRouteStore = useAsyncRouteStoreWidthOut();

  router.beforeEach(async (to, from, next) => {
    loadingBar.start();

    if (from.path === LOGIN_PATH && to.name === 'errorPage') {
      next(PageEnum.BASE_HOME);
      return;
    }

    // Whitelist can be directly entered
    if (whitePathList.includes(to.path as PageEnum)) {
      next();
      return;
    }

    const token = sessionStorage.getItem(ACCESS_TOKEN);

    if (!token) {
      userStore.logout();

      if (to.meta.ignoreAuth) {
        next();
        return;
      }

      const redirectData: { path: string; replace: boolean; query?: Recordable<string> } = {
        path: LOGIN_PATH,
        replace: true,
      };
      if (to.path) {
        redirectData.query = { ...redirectData.query, redirect: to.path };
      }
      next(redirectData);
      return;
    }

    // Routes already loaded and user info is valid - just check for mobile deployment redirect
    const hasValidUserInfo = userStore.getPermissions.length > 0;
    if (asyncRouteStore.getIsDynamicAddedRoute && hasValidUserInfo) {
      if (handleMobileDeploymentRedirect(to.path)) return;
      next();
      return;
    }

    // First login - load user info and routes
    const userInfo = await userStore.GetInfo();
    const routes = await asyncRouteStore.generateRoutes(userInfo);

    routes.forEach((item) => {
      router.addRoute(item as unknown as RouteRecordRaw);
    });

    const isErrorPage = router.getRoutes().findIndex((item) => item.name === ErrorPageRoute.name);
    if (isErrorPage === -1) {
      router.addRoute(ErrorPageRoute as unknown as RouteRecordRaw);
    }

    // Determine redirect path
    const redirectPath = (from.query.redirect || to.path) as string;
    let redirect = decodeURIComponent(redirectPath);

    // Initialize IS_MOBILE for fresh logins and sanitize redirect path
    const isMobile = sessionStorage.getItem('IS_MOBILE');
    if (isMobile === null) {
      if (userStore.isDeployment && !userStore.isSuperAdmin) {
        // Deployment users (non-super-admin) default to mobile mode
        sessionStorage.setItem('IS_MOBILE', 'true');
        redirect = '/deployment/equipment-aggregated';
      } else {
        // Super admins and non-deployment users default to desktop mode
        sessionStorage.setItem('IS_MOBILE', 'false');
        // Don't redirect to mobile-only page
        if (redirect === '/deployment/equipment-aggregated') {
          redirect = DEFAULT_HOME;
        }
      }
      // Force full page reload to ensure Header component reads new IS_MOBILE value
      asyncRouteStore.setDynamicAddedRoute(true);
      window.location.href = '/#' + redirect;
      window.location.reload();
      return;
    }

    // Check for mobile deployment redirect
    if (handleMobileDeploymentRedirect(redirect)) return;

    // Navigate to the redirect path
    asyncRouteStore.setDynamicAddedRoute(true);
    next({ path: redirect, replace: true });

    loadingBar && loadingBar.finish();
  });

  router.afterEach((to, _, failure) => {
    // handleKeepAlive(to);
    document.title = (to?.meta?.title as string) || document.title;
    if (isNavigationFailure(failure)) {
      //console.log('failed navigation', failure)
    }
    const asyncRouteStore = useAsyncRouteStoreWidthOut();
    // 在这里设置需要缓存的组件名称
    const keepAliveComponents = asyncRouteStore.keepAliveComponents;
    const currentComName: any = to.matched.find((item) => item.name == to.name)?.name;
    if (currentComName && !keepAliveComponents.includes(currentComName) && to.meta?.keepAlive) {
      // 需要缓存的组件
      keepAliveComponents.push(currentComName);
    } else if (!to.meta?.keepAlive || to.name == 'Redirect') {
      // 不需要缓存的组件
      const index = asyncRouteStore.keepAliveComponents.findIndex((name) => name == currentComName);
      if (index != -1) {
        keepAliveComponents.splice(index, 1);
      }
    }
    asyncRouteStore.setKeepAliveComponents(keepAliveComponents);
    loadingBar && loadingBar.finish();
  });

  /**
   * 递归处理多余的 layout : <router-view>
   */
  // function handleKeepAlive(to) {
  //   if (to.matched && to.matched.length > 2) {
  //     for (let i = 0; i < to.matched.length; i++) {
  //       const element = to.matched[i];
  //       if (element.components.default.name === 'Layout') {
  //         to.matched.splice(i, 1);
  //         handleKeepAlive(to);
  //       }
  //     }
  //   }
  // }
}
